import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useGetIdentity } from '@refinedev/core';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import StarIcon from '@mui/icons-material/Star';
import StarOutlineIcon from '@mui/icons-material/StarOutline';
import CloseIcon from '@mui/icons-material/Close';
import {
  createUnit,
  deleteUnit,
  listAdminUnits,
  updateUnit,
  type AdminUnit,
} from '../../lib/units-api';
import {
  listUnitResidents,
  setPrimaryResident,
  updateResident,
  deleteResident,
  type PublicResident,
} from '../../lib/residents-api';
import { extractApiError } from '../../lib/api-client';
import { useT } from '../../i18n';

interface Identity {
  permissions?: string[];
}

const UNIT_TYPES = ['HOUSE', 'APARTMENT', 'RUKO'] as const;
const OCCUPANCIES = ['OCCUPIED', 'VACANT', 'OWNER_ABSENT'] as const;

const UNIT_TYPE_LABELS: Record<string, string> = {
  HOUSE: 'units.typeHouse',
  APARTMENT: 'units.typeApartment',
  RUKO: 'units.typeRuko',
};
const OCCUPANCY_LABELS: Record<string, string> = {
  OCCUPIED: 'units.occupiedChip',
  VACANT: 'units.vacantChip',
  OWNER_ABSENT: 'units.ownerAbsentChip',
};

// Occupancy is descriptive only — nothing in the system branches on it. These
// help lines exist so two staff members entering units land on the same
// meaning, and so nobody mistakes "Vacant" for a billing exemption. The
// canonical definitions live in docs/02-schema.md.
const OCCUPANCY_HELP: Record<string, string> = {
  OCCUPIED: 'units.occHelpOccupied',
  VACANT: 'units.occHelpVacant',
  OWNER_ABSENT: 'units.occHelpOwnerAbsent',
};

export function UnitsListPage() {
  const t = useT();
  const [units, setUnits] = useState<AdminUnit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminUnit | null>(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState<AdminUnit | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [viewingUnit, setViewingUnit] = useState<AdminUnit | null>(null);

  const { data: identity } = useGetIdentity<Identity>();
  const canManageResidents = (identity?.permissions ?? []).includes('can_manage_residents');

  async function confirmDelete() {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteUnit(deleting.id);
      setInfo(t('units.deletedInfo', { label: deleting.label }));
      setDeleting(null);
      refresh();
    } catch (e) {
      const { code, message } = extractApiError(e);
      if (code === 'ERR_NOT_FOUND') {
        // Already gone (concurrent delete / stale list) — treat as success.
        setInfo(t('units.alreadyRemoved'));
        setDeleting(null);
        refresh();
      } else {
        // 409 blocker (has residents/invoices/etc.) — keep dialog open, show why.
        setDeleteError(message);
      }
    } finally {
      setDeleteBusy(false);
    }
  }

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      setUnits(await listAdminUnits());
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    refresh();
  }, []);

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <div>
          <Typography variant="h4" fontWeight={600}>
            {t('units.title')}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t('units.subtitle')}
            {canManageResidents ? ' ' + t('units.subtitleResidentsHint') : ''}
          </Typography>
        </div>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreating(true)}>
          {t('units.newUnit')}
        </Button>
      </Stack>

      {error ? <Alert severity="error">{error}</Alert> : null}
      {info ? (
        <Alert severity="info" onClose={() => setInfo(null)}>
          {info}
        </Alert>
      ) : null}

      <Card variant="outlined">
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          {loading ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <CircularProgress />
            </Box>
          ) : units.length === 0 ? (
            <Box sx={{ p: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">
                {t('units.empty')}
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('units.colBlock')}</TableCell>
                    <TableCell>{t('units.colNumber')}</TableCell>
                    <TableCell>{t('units.colLabel')}</TableCell>
                    <TableCell sx={{ width: 140 }}>{t('units.colType')}</TableCell>
                    <TableCell sx={{ width: 160 }}>{t('units.colOccupancy')}</TableCell>
                    <TableCell align="right" sx={{ width: 120 }}>
                      {t('common.actions')}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {units.map((u) => (
                    <TableRow
                      key={u.id}
                      hover
                      onClick={canManageResidents ? () => setViewingUnit(u) : undefined}
                      sx={{ cursor: canManageResidents ? 'pointer' : 'default' }}
                    >
                      <TableCell>{u.block}</TableCell>
                      <TableCell>{u.number}</TableCell>
                      <TableCell>{u.label}</TableCell>
                      <TableCell>
                        <Chip size="small" label={t(UNIT_TYPE_LABELS[u.type] ?? u.type)} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <OccupancyChip occupancy={u.occupancy} />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title={t('common.edit')}>
                          <IconButton size="small" onClick={(e) => { e.stopPropagation(); setEditing(u); }}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title={t('common.delete')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteError(null);
                              setDeleting(u);
                            }}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <UnitResidentsDialog unit={viewingUnit} onClose={() => setViewingUnit(null)} />

      <UnitDialog
        open={creating}
        unit={null}
        onClose={() => setCreating(false)}
        onSaved={() => {
          setCreating(false);
          refresh();
        }}
      />
      <UnitDialog
        open={!!editing}
        unit={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          refresh();
        }}
      />

      <Dialog
        open={!!deleting}
        onClose={() => {
          if (!deleteBusy) setDeleting(null);
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {t('units.deleteTitle', { label: deleting?.label })}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              {t('units.deleteWarning')}
            </Typography>
            {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={deleteBusy}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deleteBusy}>
            {deleteBusy ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

function ResidentStatusChip({ status }: { status: PublicResident['status'] }) {
  const t = useT();
  const color = status === 'ACTIVE' ? 'success' : status === 'PENDING' ? 'warning' : 'default';
  const label =
    status === 'ACTIVE'
      ? t('res.chipActive')
      : status === 'PENDING'
        ? t('res.chipPending')
        : t('res.chipSuspended');
  return <Chip size="small" label={label} color={color} variant="outlined" />;
}

// Dialog: residents linked to a unit — set primary, suspend/reactivate, delete.
function UnitResidentsDialog({ unit, onClose }: { unit: AdminUnit | null; onClose: () => void }) {
  const t = useT();
  const [residents, setResidents] = useState<PublicResident[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<PublicResident | null>(null);
  const [deleteBusy, setDeleteBusy] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  useEffect(() => {
    if (!unit) return;
    setError(null);
    setBusyId(null); // never carry a stuck busy state into a freshly opened dialog
    setLoading(true);
    listUnitResidents(unit.id)
      .then(setResidents)
      .catch((e) => setError(extractApiError(e).message))
      .finally(() => setLoading(false));
  }, [unit]);

  async function makePrimary(userId: string) {
    if (!unit) return;
    setBusyId(userId);
    setError(null);
    try {
      setResidents(await setPrimaryResident(unit.id, userId));
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setBusyId(null);
    }
  }

  async function setStatus(r: PublicResident, status: 'ACTIVE' | 'SUSPENDED') {
    setBusyId(r.id);
    setError(null);
    try {
      const updated = await updateResident(r.id, { status });
      setResidents((prev) => prev.map((x) => (x.id === r.id ? updated : x)));
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setBusyId(null);
    }
  }

  async function confirmDelete() {
    if (!deleting) return;
    setDeleteBusy(true);
    setDeleteError(null);
    try {
      await deleteResident(deleting.id);
      setResidents((prev) => prev.filter((x) => x.id !== deleting.id));
      setDeleting(null);
    } catch (e) {
      const { code, message } = extractApiError(e);
      if (code === 'ERR_NOT_FOUND') {
        setResidents((prev) => prev.filter((x) => x.id !== deleting.id));
        setDeleting(null);
      } else {
        setDeleteError(message);
      }
    } finally {
      setDeleteBusy(false);
    }
  }

  return (
    <>
      <Dialog open={!!unit} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <span>{t('units.residentsTitle', { label: unit?.label })}</span>
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {t('units.residentsHelper')}
          </Typography>
          {error ? <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert> : null}
          {loading ? (
            <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress size={24} /></Box>
          ) : residents.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('units.noResidentsLinked')}
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t('common.colName')}</TableCell>
                    <TableCell>{t('common.email')}</TableCell>
                    <TableCell>{t('common.status')}</TableCell>
                    <TableCell align="center">{t('units.colPrimary')}</TableCell>
                    <TableCell align="right">{t('common.actions')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {residents.map((r) => (
                    <TableRow key={r.id} hover>
                      <TableCell>{r.full_name}</TableCell>
                      <TableCell>{r.email}</TableCell>
                      <TableCell><ResidentStatusChip status={r.status} /></TableCell>
                      <TableCell align="center">
                        {r.is_primary ? (
                          <Tooltip title={t('units.primaryTooltip')}>
                            <Chip size="small" color="primary" icon={<StarIcon fontSize="small" />} label={t('units.primaryChip')} />
                          </Tooltip>
                        ) : (
                          <Tooltip title={t('units.makePrimary')}>
                            <span>
                              <IconButton size="small" onClick={() => makePrimary(r.id)} disabled={busyId === r.id}>
                                {busyId === r.id ? <CircularProgress size={18} /> : <StarOutlineIcon />}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
                          {r.status === 'SUSPENDED' ? (
                            <Button size="small" onClick={() => setStatus(r, 'ACTIVE')} disabled={busyId === r.id}>
                              {t('res.reactivate')}
                            </Button>
                          ) : (
                            <Button size="small" color="error" onClick={() => setStatus(r, 'SUSPENDED')} disabled={busyId === r.id}>
                              {t('res.suspend')}
                            </Button>
                          )}
                          <Tooltip title={t('common.delete')}>
                            <span>
                              <IconButton
                                size="small" color="error"
                                onClick={() => { setDeleteError(null); setDeleting(r); }}
                                disabled={busyId === r.id}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{t('common.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!deleting} onClose={() => !deleteBusy && setDeleting(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('res.deleteTitle', { name: deleting?.full_name })}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2">
              {t('res.deleteWarning')}
            </Typography>
            {deleteError ? <Alert severity="error">{deleteError}</Alert> : null}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleting(null)} disabled={deleteBusy}>{t('common.cancel')}</Button>
          <Button onClick={confirmDelete} color="error" variant="contained" disabled={deleteBusy}>
            {deleteBusy ? t('common.deleting') : t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

function OccupancyChip({ occupancy }: { occupancy: AdminUnit['occupancy'] }) {
  const t = useT();
  const color =
    occupancy === 'OCCUPIED' ? 'success' : occupancy === 'VACANT' ? 'default' : 'warning';
  return <Chip size="small" label={t(OCCUPANCY_LABELS[occupancy] ?? occupancy)} color={color} />;
}

function UnitDialog({
  open,
  unit,
  onClose,
  onSaved,
}: {
  open: boolean;
  unit: AdminUnit | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const t = useT();
  const isEdit = !!unit;
  const [block, setBlock] = useState('');
  const [number, setNumber] = useState('');
  const [label, setLabel] = useState('');
  const [type, setType] = useState<(typeof UNIT_TYPES)[number]>('HOUSE');
  const [occupancy, setOccupancy] = useState<(typeof OCCUPANCIES)[number]>('OCCUPIED');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Seed the form when the dialog opens.
  useEffect(() => {
    if (!open) return;
    setError(null);
    setBlock(unit?.block ?? '');
    setNumber(unit?.number ?? '');
    setLabel(unit?.label ?? '');
    setType((unit?.type as (typeof UNIT_TYPES)[number]) ?? 'HOUSE');
    setOccupancy((unit?.occupancy as (typeof OCCUPANCIES)[number]) ?? 'OCCUPIED');
  }, [open, unit]);

  const canSave = isEdit ? true : block.trim() !== '' && number.trim() !== '';

  async function save() {
    setBusy(true);
    setError(null);
    try {
      if (isEdit && unit) {
        await updateUnit(unit.id, { label: label.trim(), type, occupancy });
      } else {
        await createUnit({
          block: block.trim(),
          number: number.trim(),
          ...(label.trim() ? { label: label.trim() } : {}),
          type,
          occupancy,
        });
      }
      onSaved();
    } catch (e) {
      setError(extractApiError(e).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>{isEdit ? t('units.editTitle') : t('units.createTitle')}</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Stack direction="row" spacing={2}>
            <TextField
              label={t('units.block')}
              value={block}
              onChange={(e) => setBlock(e.target.value)}
              disabled={isEdit}
              required={!isEdit}
              placeholder="A"
              fullWidth
            />
            <TextField
              label={t('units.number')}
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              disabled={isEdit}
              required={!isEdit}
              placeholder="18"
              fullWidth
            />
          </Stack>
          <TextField
            label={t('units.labelOptional')}
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={t('units.labelPlaceholder')}
            fullWidth
          />
          <TextField
            select
            label={t('units.type')}
            value={type}
            onChange={(e) => setType(e.target.value as (typeof UNIT_TYPES)[number])}
            fullWidth
          >
            {UNIT_TYPES.map((ut) => (
              <MenuItem key={ut} value={ut}>
                {t(UNIT_TYPE_LABELS[ut] ?? ut)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            select
            label={t('units.occupancy')}
            value={occupancy}
            onChange={(e) => setOccupancy(e.target.value as (typeof OCCUPANCIES)[number])}
            helperText={t(OCCUPANCY_HELP[occupancy] ?? '')}
            fullWidth
          >
            {OCCUPANCIES.map((o) => (
              <MenuItem key={o} value={o}>
                {t(OCCUPANCY_LABELS[o] ?? o)}
              </MenuItem>
            ))}
          </TextField>
          {isEdit ? (
            <Typography variant="caption" color="text.secondary">
              {t('units.blockCannotEdit')}
            </Typography>
          ) : null}
          {error ? <Alert severity="error">{error}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel')}
        </Button>
        <Button onClick={save} variant="contained" disabled={!canSave || busy}>
          {busy ? t('common.saving') : isEdit ? t('common.save') : t('common.create')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
